import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../traderhub24-logo.svg";
import Footer from "./components/Footer";

function TermsAndConditions({ onBack, onNavigate }: { onBack: () => void; onNavigate?: (page: "home" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => void }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-app-bg text-gray-900">
      {/* Header */}
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Terms & Conditions</h1>
          <p className="text-gray-600 mb-8">
            Last Updated: 2025-12-17
          </p>

          <div className="prose prose-gray max-w-none space-y-6">
            <p className="text-gray-700 leading-relaxed">
              These Terms & Conditions ("Terms") govern your access to and use of the application and related services ("TraderHub24").
            </p>
            <p className="text-gray-700 leading-relaxed">
              By creating an account or using the TraderHub24, you agree to be bound by these Terms.
            </p>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">1. Eligibility</h2>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>You must be at least 18 years old to use TraderHub24.</li>
                <li>By using TraderHub24, you confirm that you have the legal capacity to enter into a binding agreement.</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">2. Nature of the Service</h2>
              <p className="text-gray-700 mb-2">TraderHub24 provides:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Trading tools</li>
                <li>Performance analytics</li>
                <li>Risk and position sizing calculations</li>
                <li>Market-related informational features</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-2">
                TraderHub24 does NOT provide financial, investment, or trading advice.
              </p>
              <p className="text-gray-700 mb-2">
                All content is for educational and analytical purposes only.
              </p>
              <p className="text-gray-700">
                You are solely responsible for your trading decisions and outcomes.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">3. Account Registration</h2>
              <p className="text-gray-700 mb-2">To access certain features, you must create an account.</p>
              <p className="text-gray-700 mb-2">You agree to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Provide accurate and up-to-date information</li>
                <li>Maintain the confidentiality of your login credentials</li>
                <li>Be responsible for all activities under your account</li>
              </ul>
              <p className="text-gray-700 mt-2">
                We reserve the right to suspend or terminate accounts that violate these Terms.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">4. User Responsibilities</h2>
              <p className="text-gray-700 mb-2">You agree not to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Use TraderHub24 for unlawful purposes</li>
                <li>Attempt to reverse-engineer, copy, or exploit the platform</li>
                <li>Interfere with system security or performance</li>
                <li>Upload malicious code or abusive content</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Any misuse may result in immediate account termination.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">5. Trading Risk Disclaimer</h2>
              <p className="text-gray-700 mb-2">
                Trading financial instruments involves significant risk and may result in losses exceeding initial capital.
              </p>
              <p className="text-gray-700 mb-2">You acknowledge that:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Past performance is not indicative of future results</li>
                <li>Market data may be delayed or inaccurate</li>
                <li>Calculations and analytics may contain errors</li>
                <li>TraderHub24 is used entirely at your own risk.</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">6. Intellectual Property</h2>
              <p className="text-gray-700 mb-2">
                All content, software, designs, trademarks, and analytics logic are the exclusive property of the TraderHub24 owner.
              </p>
              <p className="text-gray-700">
                You are granted a limited, non-exclusive, non-transferable license to use TraderHub24 for personal or internal business use only.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">7. Subscription, Payments & Refunds</h2>
              <p className="text-gray-700 mb-2">If TraderHub24 includes paid features:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Prices and billing cycles are clearly disclosed before purchase</li>
                <li>Payments are processed via third-party providers (e.g. Stripe)</li>
                <li>Subscriptions renew automatically unless canceled</li>
                <li>Refunds, if applicable, are governed by applicable consumer protection laws and stated refund policies.</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">8. Service Availability</h2>
              <p className="text-gray-700 mb-2">We aim to provide uninterrupted access but do not guarantee:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Continuous availability</li>
                <li>Error-free operation</li>
                <li>Data accuracy at all times</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Maintenance, updates, or technical issues may result in temporary downtime.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">9. Limitation of Liability</h2>
              <p className="text-gray-700 mb-2">To the maximum extent permitted by law:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>We are not liable for trading losses</li>
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>Total liability shall not exceed the amount paid by the user in the last 12 months</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">10. Termination</h2>
              <p className="text-gray-700 mb-2">You may terminate your account at any time.</p>
              <p className="text-gray-700 mb-2">We may suspend or terminate access:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>For violations of these Terms</li>
                <li>For legal or regulatory reasons</li>
                <li>To protect system integrity</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Upon termination, access to the Service will cease.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">11. Privacy</h2>
              <p className="text-gray-700">
                Your use of TraderHub24 is also governed by our Privacy Policy, which explains how we collect and process personal data.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">12. Modifications to the Terms</h2>
              <p className="text-gray-700 mb-2">We may update these Terms from time to time.</p>
              <p className="text-gray-700">
                Continued use of TraderHub24 after changes constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">13. Governing Law and Jurisdiction</h2>
              <p className="text-gray-700 mb-2">
                These Terms shall be governed by and construed in accordance with the laws of Sweden.
              </p>
              <p className="text-gray-700">
                Any disputes shall be subject to the exclusive jurisdiction of the courts of Stockholm, Sweden.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer currentPage="terms-and-conditions" onNavigate={onNavigate || ((page) => {
        if (page === "terms-and-conditions") return;
        else if (page === "privacy-policy") navigate("/privacy");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") navigate("/changelog");
      })} />
    </div>
  );
}

export default TermsAndConditions;
